import { useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SupportedCurrencies, withStyles } from 'vopay';
import { useToast } from 'shared';
import { BodyText, Button, Card, NoFillButton, Skeleton, Title } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import useAutoBalanceTransfer from '../useAutoBalanceTransfer';
import useFetchPayoutAccount from '../useFetchPayoutAccount';
import PayoutAccountField from './PayoutAccountField';
import SetUpTransferTitle from './SetUpTransferTitle';
import TransferReview from './TransferReview';
import useTransferFormConfig, {
  EMPTY_TRANSFER_FORM,
  TransferRequest,
  transferFormValues,
} from './useTransferFormConfig';
import styles from './SetUpTransferPage.module.scss';

const cn = withStyles(styles);

const SUPPORTED_CURRENCIES: SupportedCurrencies[] = ['CAD', 'USD'];

type SetUpTransferPageProps = {
  mode: 'create' | 'update';
};

export default function SetUpTransferPage({ mode }: SetUpTransferPageProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.SetUpTransfer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const toast = useToast();

  const { currency } = useParams<{ currency: string }>();
  const { state } = useLocation<{ isCurrencyNamed?: boolean } | undefined>();

  const {
    response: { Country, clientAccountsEnabled },
  } = useClientData();

  const { payoutAccount: operationalBankAccount, fetchPayoutAccount } = useFetchPayoutAccount();
  const {
    autoBalanceTransfer: existingTransfer,
    fetchAutoBalanceTransfer,
    createAutoBalanceTransfer,
  } = useAutoBalanceTransfer();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);

  const isUpdate = mode === 'update';
  const scheduleCurrency = SUPPORTED_CURRENCIES.find((supported) => supported === currency);
  // The overview says whether it shows tabs. On a deep link, only the country currency can be the sole tab.
  const isCurrencyNamed = state?.isCurrencyNamed ?? scheduleCurrency !== (Country === 'US' ? 'USD' : 'CAD');

  const saveTransfer = async (values: TransferRequest) => {
    try {
      await createAutoBalanceTransfer({ ...values, Currency: scheduleCurrency });

      toast.success(
        isUpdate
          ? t('updateSuccess', { defaultValue: 'Your auto balance transfer is updated.' })
          : t('createSuccess', { defaultValue: 'Your auto balance transfer is set up.' }),
      );

      history.push('/auto-balance-transfer', { currency: scheduleCurrency });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : tGlobal('genericError');

      toast.neutral(
        isUpdate
          ? t('updateError', {
              defaultValue: 'Could not update your auto balance transfer: {{errorMessage}}',
              errorMessage,
            })
          : t('createError', {
              defaultValue: 'Could not set up your auto balance transfer: {{errorMessage}}',
              errorMessage,
            }),
      );
    }
  };

  const { ctx, renderFields, renderSettings } = useTransferFormConfig({
    initialValues: existingTransfer ? transferFormValues(existingTransfer) : EMPTY_TRANSFER_FORM,
    canSplitByCounterparty: !clientAccountsEnabled,
    onSubmit: saveTransfer,
  });

  useEffect(() => {
    if (!scheduleCurrency) return;

    const load = async () => {
      const [operationalAccountResult, transferResult] = await Promise.allSettled([
        fetchPayoutAccount({ Currency: scheduleCurrency }),
        isUpdate ? fetchAutoBalanceTransfer({ Currency: scheduleCurrency }) : undefined,
      ]);

      if (operationalAccountResult.status === 'rejected') {
        const { reason } = operationalAccountResult;
        const errorMessage = reason instanceof Error ? reason.message : tGlobal('genericError');

        setHasLoadFailed(true);

        toast.neutral(
          t('fetchOperationalAccountError', {
            defaultValue: 'Could not load your operational bank account: {{errorMessage}}',
            errorMessage,
          }),
        );
      }

      if (transferResult.status === 'rejected') {
        const { reason } = transferResult;
        const errorMessage = reason instanceof Error ? reason.message : tGlobal('genericError');

        setHasLoadFailed(true);

        toast.neutral(
          t('fetchTransferError', {
            defaultValue: 'Could not load your auto balance transfer: {{errorMessage}}',
            errorMessage,
          }),
        );
      }

      setHasLoaded(true);
    };

    load();
  }, [currency, isUpdate]);

  if (!scheduleCurrency || hasLoadFailed) return <Redirect to="/auto-balance-transfer" />;

  // Nothing to edit: the overview offers set-up instead.
  if (hasLoaded && isUpdate && !existingTransfer) return <Redirect to="/auto-balance-transfer" />;

  // Operational Accounts is closed to client-accounts users, who pay out to the primary client account instead.
  if (hasLoaded && !operationalBankAccount) {
    return <Redirect to={clientAccountsEnabled ? '/auto-balance-transfer' : '/operational-accounts'} />;
  }

  if (isReviewing && operationalBankAccount) {
    return (
      <div className={cn('page')}>
        <TransferReview
          title={
            isUpdate
              ? t('updateReviewTitle', { defaultValue: 'Review Your Changes' })
              : isCurrencyNamed
                ? t('reviewTitleCurrency', {
                    defaultValue: '{{currency}} Auto Balance Transfer Review',
                    currency: scheduleCurrency,
                  })
                : t('reviewTitle', { defaultValue: 'Auto Balance Transfer Review' })
          }
          transfer={{
            ScheduleStartDate: format(new Date(Number(ctx.values.ScheduleStartDate)), 'yyyy-MM-dd'),
            NameOfFrequency: ctx.values.TypeOfFrequency,
            AutoBalanceTransferAmount: ctx.values.AutoBalanceTransferAmount,
            Currency: scheduleCurrency,
            SplitByCounterparty: ctx.values.SplitByCounterparty,
          }}
          bankAccount={operationalBankAccount}
        />

        <div className={cn('reviewFooter')}>
          <NoFillButton id="auto-balance-transfer-back" onClick={() => setIsReviewing(false)}>
            {tGlobal('Buttons.back')}
          </NoFillButton>

          <Button
            id={isUpdate ? 'auto-balance-transfer-save' : 'auto-balance-transfer-create'}
            colorScheme="primary"
            disabled={ctx.isSubmitting}
            onClick={() => ctx.handleSubmit()}
          >
            {isUpdate
              ? t('saveChangesButton', { defaultValue: 'Save Changes' })
              : t('createButton', { defaultValue: 'Create Transfer' })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('page')}>
      <SetUpTransferTitle isUpdate={isUpdate} currency={isCurrencyNamed ? scheduleCurrency : undefined} />

      {!hasLoaded || !operationalBankAccount ? (
        <Skeleton height={480} borderRadius={10} />
      ) : (
        <Card hoverEffect={false} className={cn('card')}>
          <div className={cn('header')}>
            <Title level="3">{t('Schedule.title', { defaultValue: 'Schedule' })}</Title>

            <BodyText className={cn('description')}>
              {t('Schedule.description', { defaultValue: 'Transfers run automatically from the start date.' })}
            </BodyText>
          </div>

          <div className={cn('body')}>
            <div className={cn('fields')}>
              <PayoutAccountField currency={scheduleCurrency} account={operationalBankAccount} />
              {renderFields()}
            </div>

            <div className={cn('settings')}>{renderSettings()}</div>
          </div>

          <div className={cn('footer')}>
            <NoFillButton
              id="auto-balance-transfer-cancel"
              onClick={() => history.push('/auto-balance-transfer', { currency: scheduleCurrency })}
            >
              {tGlobal('Buttons.cancel')}
            </NoFillButton>

            <Button
              id="auto-balance-transfer-continue"
              colorScheme="primary"
              disabled={!ctx.isValid}
              onClick={() => setIsReviewing(true)}
            >
              {tGlobal('Buttons.continue')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
